import { initSwipers } from "src/vendors/swiper";
import type { InitSwiperProps } from "src/vendors/swiper";
import { PRODUCTS_BY_TAG_QUERY } from "./query";
import { STOREFRONT_ENDPOINT } from "../consts";
import { renderProductItems } from "./render";
import { ProductNode } from "./types";

const hasKey = <T extends string>(o: unknown, k: T): o is Record<T, unknown> =>
  typeof o === "object" && o !== null && k in o;

const REQUEST_TIMEOUT_MS = 15000;
const groupCache = new Map<string, ProductNode[]>();

const PRODUCT_GROUPS_SETTINGS: InitSwiperProps = {
  wrapperSelector: ".product-groups__wrapper",
  containerSelector: ".product-groups__slider",
  prevButtonSelector: ".product-groups__button-prev",
  nextButtonSelector: ".product-groups__button-next",
};

export const initProductGroupsSwipers = () => {
  initSwipers(PRODUCT_GROUPS_SETTINGS, {
    loop: false,
    spaceBetween: 10,
    slidesPerView: 3,
  });
};

async function fetchProductsByTag(tag: string): Promise<ProductNode[]> {
  if (groupCache.has(tag)) return groupCache.get(tag)!;

  if (!window.STOREFRONT_TOKEN) return [];

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(STOREFRONT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": window.STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: PRODUCTS_BY_TAG_QUERY,
        variables: { query: `tag:${JSON.stringify(tag)}` },
      }),
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timeout);
    return [];
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) return [];

  const payload = await res.json();

  if (!hasKey(payload, "data")) return [];

  const data = payload.data;

  if (!hasKey(data, "products")) return [];

  const products = data.products;

  if (!hasKey(products, "nodes") || !Array.isArray(products.nodes)) return [];

  groupCache.set(tag, products.nodes);

  return products.nodes;
}

async function initProductGroups(groupContainer: HTMLElement) {
  const groupTag = groupContainer.getAttribute("data-group-tag");
  const currentHandle =
    groupContainer.getAttribute("data-current-handle") || "";
  if (!groupTag) return;

  const sliderWrapper = groupContainer.querySelector<HTMLElement>(
    "[data-slider-wrapper]"
  );

  const products = await fetchProductsByTag(groupTag);

  const uniqueProducts = products.filter((product) => {
    if (product.handle === currentHandle) return false;
    return true;
  });

  if (!uniqueProducts.length) return;

  if (sliderWrapper) {
    sliderWrapper.innerHTML = renderProductItems(uniqueProducts);
  }
  groupContainer.style.display = "block";
}

function mountProductGroups(root: ParentNode = document) {
  const containers = root.querySelectorAll<HTMLElement>("[data-groups]");
  containers.forEach((container) => {
    initProductGroups(container);
  });
  initProductGroupsSwipers();
}

const init = () => mountProductGroups();
document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", init)
  : init();

document.addEventListener("shopify:section:load", () => mountProductGroups());
